import React, { useEffect, useMemo, useRef, useState } from "react";

/** Replace with your real imports */
import img1 from "../../assets/gallery/House1.jpeg";
import img2 from "../../assets/gallery/Bathroom.jpg";
import img3 from "../../assets/gallery/treppe.jpeg";
import img4 from "../../assets/gallery/Büroreinigung.jpg";
import img5 from "../../assets/gallery/Fensterreinigung.jpg";
import img6 from "../../assets/gallery/Bathroom2.jpg";
import img7 from "../../assets/gallery/Bauendreinigung.jpg";
import img8 from "../../assets/gallery/Grünpflege.jpg";

/** Gallery data: add category + (optional) height hints for nicer masonry */
const IMAGES = [
  { src: img1, alt: "Wohnzimmer", category: "Wohnraum", h: 360 },
  { src: img2, alt: "Badezimmer", category: "Sanitär", h: 420 },
  { src: img3, alt: "Treppenhausreinigung ", category: "Treppenhaus", h: 380 },
  { src: img4, alt: "Büroreinigung ", category: "Büro", h: 440 },
  { src: img5, alt: "Fensterreinigung ", category: "Fenster", h: 320 },
  { src: img6, alt: "Badezimmer", category: "Sanitär", h: 440 },
  { src: img7, alt: "Bauendreinigung ", category: "Büro", h: 400 },
  { src: img8, alt: "Grünpflege ", category: "Grünpflege ", h: 420 },
];

/** All categories (chips) */
const CATEGORIES = ["Alle", "Wohnraum", "Büro", "Sanitär", "Fenster", "Grünpflege ", "Treppenhaus"];

/** Small icon helpers (inline SVG, no libs) */
const Icons = {
  search: (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="M21 21l-4.3-4.3" />
    </svg>
  ),
  close: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path strokeLinecap="round" d="M6 6l12 12M6 18L18 6" />
    </svg>
  ),
  left: (
    <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path strokeLinecap="round" d="M15 6l-6 6 6 6" />
    </svg>
  ),
  right: (
    <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path strokeLinecap="round" d="M9 18l6-6-6-6" />
    </svg>
  ),
  zoomIn: (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 21l-4.3-4.3" />
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="M10.5 8v5M8 10.5h5" />
    </svg>
  ),
  zoomOut: (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 21l-4.3-4.3" />
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="M13 10.5H8" />
    </svg>
  ),
};

export default function Gallery() {
  const [filter, setFilter] = useState("Alle");
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [loaded, setLoaded] = useState(() => new Set()); // track loaded images for skeleton

  const filtered = useMemo(
    () => (filter === "Alle" ? IMAGES : IMAGES.filter((i) => i.category === filter)),
    [filter]
  );

  const openLightbox = (i) => {
    setIndex(i);
    setOpen(true);
  };

  return (
<section id="gallery" className="bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        {/* Header */}
        <header className="mb-10 sm:mb-12 text-center">
          <span className="inline-flex items-center text-emerald-600 font-semibold uppercase tracking-[0.18em] text-xs sm:text-sm">
            <span className="w-8 sm:w-10 h-[2px] bg-emerald-600 rounded mr-3" />
            Galerie
            <span className="w-8 sm:w-10 h-[2px] bg-emerald-600 rounded ml-3" />
          </span>
          <h2 className="mt-4 text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900">
            Einblicke in unsere Arbeit
          </h2>
          <p className="mt-4 max-w-2xl mx-auto text-gray-600 text-base sm:text-lg">
            Klicken Sie auf ein Foto zur Vollansicht – navigieren Sie mit Pfeiltasten, Wischen oder Miniaturen.
          </p>
        </header>

        {/* Filters */}
        <div className="mb-8 flex flex-wrap items-center justify-center gap-2 sm:gap-3">
          {CATEGORIES.map((c) => {
            const active = c === filter;
            return (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`inline-flex items-center rounded-full border px-3 sm:px-4 py-1.5 text-xs sm:text-sm transition
                  ${active ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"}`}
                aria-pressed={active}
              >
                {c}
              </button>
            );
          })}
        </div>

        {/* Masonry (CSS columns) */}
        <div className="columns-2 gap-4 sm:columns-3 sm:gap-5 lg:columns-4">
          {filtered.map((img, i) => (
            <figure
              key={`${img.src}-${i}`}
              className="relative mb-4 break-inside-avoid overflow-hidden rounded-xl border border-gray-200 bg-gray-100"
            >
              <button
                onClick={() => openLightbox(i)}
                className="group block w-full focus:outline-none focus-visible:ring-4 focus-visible:ring-emerald-400/40"
                aria-label={`Open image: ${img.alt}`}
              >
                {/* skeleton shimmer until loaded */}
                {!loaded.has(i) && (
                  <div
                    style={{ height: img.h || 360 }}
                    className="w-full animate-pulse bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200"
                  />
                )}
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  onLoad={() => {
                    setLoaded((prev) => {
                      const n = new Set(prev);
                      n.add(i);
                      return n;
                    });
                  }}
                  className={`w-full object-cover transition-transform duration-500 group-hover:scale-[1.03] ${
                    loaded.has(i) ? "opacity-100" : "opacity-0"
                  }`}
                  style={{ height: img.h || "auto" }}
                />

                {/* overlay with caption + zoom icon */}
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/35 via-black/0 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
                <figcaption className="absolute inset-x-0 bottom-0 flex items-center justify-between p-3">
                  <span className="text-white/95 drop-shadow font-medium text-[13px] sm:text-sm line-clamp-1">
                    {img.alt}
                  </span>
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-gray-800 shadow-md">
                    {Icons.search}
                  </span>
                </figcaption>

                {/* category pill */}
                <span className="absolute left-3 top-3 rounded-full bg-white/90 px-2 py-0.5 text-[11px] font-medium text-gray-700 shadow">
                  {img.category}
                </span>
              </button>
            </figure>
          ))}
        </div>

        {/* Lightbox */}
        {open && (
          <Lightbox
            images={filtered}
            index={index}
            setIndex={setIndex}
            onClose={() => setOpen(false)}
          />
        )}
      </div>
    </section>
  );
}

/* =========================
   Premium Lightbox
   ========================= */
function Lightbox({ images, index, setIndex, onClose }) {
  const [zoom, setZoom] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [start, setStart] = useState({ x: 0, y: 0 });
  const [startOffset, setStartOffset] = useState({ x: 0, y: 0 });
  const [touchX, setTouchX] = useState(0);
  const [showInfo, setShowInfo] = useState(true);

  const wrapRef = useRef(null);

  const prev = () => setIndex((i) => (i + images.length - 1) % images.length);
  const next = () => setIndex((i) => (i + 1) % images.length);

  // Preload neighbors
  useEffect(() => {
    const preA = new Image();
    preA.src = images[(index + 1) % images.length].src;
    const preB = new Image();
    preB.src = images[(index + images.length - 1) % images.length].src;
  }, [index, images]);

  // a11y + body lock
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
      if (e.key.toLowerCase() === "z") toggleZoom();
      if (e.key.toLowerCase() === "i") setShowInfo((s) => !s);
    }
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    wrapRef.current?.focus();
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  // Touch swipe
  const onTouchStart = (e) => setTouchX(e.touches[0].clientX);
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touchX;
    if (dx > 50) prev();
    if (dx < -50) next();
  };

  // Zoom
  const toggleZoom = () => {
    if (zoom) {
      setZoom(false);
      setOffset({ x: 0, y: 0 });
    } else setZoom(true);
  };

  // Drag pan
  const onMouseDown = (e) => {
    if (!zoom) return;
    e.preventDefault();
    setDragging(true);
    setStart({ x: e.clientX, y: e.clientY });
    setStartOffset(offset);
  };
  const onMouseMove = (e) => {
    if (!zoom || !dragging) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    setOffset({ x: startOffset.x + dx, y: startOffset.y + dy });
  };
  const onMouseUp = () => setDragging(false);

  // Clamp offset (basic)
  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const clampedX = clamp(offset.x, -2000, 2000);
  const clampedY = clamp(offset.y, -2000, 2000);

  // Share helper
  async function shareCurrent() {
    const it = images[index];
    try {
      if (navigator.share) {
        await navigator.share({ title: it.alt, url: it.src });
      } else {
        await navigator.clipboard.writeText(it.src);
        alert("Bild-URL in die Zwischenablage kopiert.");
      }
    } catch {
      /* user cancelled */
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Bildgalerie"
      onClick={onClose}
    >
      <div
        ref={wrapRef}
        tabIndex={-1}
        className="relative mx-auto flex w-full max-w-[1200px] flex-col px-3 sm:px-6 outline-none"
        onClick={(e) => e.stopPropagation()}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
      >
        {/* Top toolbar */}
        <div className="mb-3 flex w-full items-center justify-between text-white">
          <div className="inline-flex items-center gap-2 text-xs sm:text-sm">
            <span className="rounded-full bg-white/10 px-2 py-1">{index + 1} / {images.length}</span>
            {showInfo && (
              <span className="hidden sm:inline-block text-white/80">
                {images[index].alt}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <ToolBtn onClick={() => setShowInfo((s) => !s)} label="Info (I)">
              {/* info icon */}
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 8h.01M11 12h2v4h-2z" />
              </svg>
            </ToolBtn>

            <ToolBtn onClick={toggleZoom} label={zoom ? "Zoom zurücksetzen (Z)" : "Zoom (Z)"}>
              {zoom ? Icons.zoomOut : Icons.zoomIn}
            </ToolBtn>

      


            <button
              onClick={onClose}
              className="rounded-md bg-white/10 px-2.5 py-2 hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/50"
              aria-label="Schließen"
              title="Schließen"
            >
              {Icons.close}
            </button>
          </div>
        </div>

        {/* Main image area */}
        <div
          className="relative flex min-h-[52vh] sm:min-h-[62vh] items-center justify-center"
          onTouchStart={(e) => setTouchX(e.touches[0].clientX)}
          onTouchEnd={(e) => {
            const dx = e.changedTouches[0].clientX - touchX;
            if (dx > 50) prev();
            if (dx < -50) next();
          }}
          onMouseDown={onMouseDown}
        >
          {/* Prev */}
          <SideNav onClick={prev} aria="Vorheriges Bild">
            {Icons.left}
          </SideNav>

          {/* Image */}
          <img
            src={images[index].src}
            alt={images[index].alt}
            className={`max-h-[78vh] w-auto select-none rounded-lg shadow-2xl transition-transform duration-200 ${zoom ? "cursor-grab active:cursor-grabbing" : "cursor-zoom-in"}`}
            style={{ transform: `translate(${clampedX}px, ${clampedY}px) scale(${zoom ? 1.5 : 1})`, transformOrigin: "center" }}
            draggable={false}
            onClick={toggleZoom}
          />

          {/* Next */}
          <SideNav right onClick={next} aria="Nächstes Bild">
            {Icons.right}
          </SideNav>
        </div>

        {/* Info line (mobile visible) */}
        {showInfo && (
          <p className="mt-3 text-center text-xs sm:text-sm text-white/85">{images[index].alt}</p>
        )}

        {/* Filmstrip thumbnails */}
        <div className="mt-5 flex items-center justify-center">
          <div className="flex max-w-full gap-2 overflow-x-auto p-1">
            {images.map((im, i) => {
              const active = i === index;
              return (
                <button
                  key={`${im.src}-${i}`}
                  onClick={() => setIndex(i)}
                  className={`relative h-16 w-24 flex-shrink-0 overflow-hidden rounded-lg border transition
                    ${active ? "border-emerald-400 ring-2 ring-emerald-300" : "border-white/20 hover:brightness-110"}`}
                  aria-label={`Öffne Miniatur ${i + 1}`}
                >
                  <img src={im.src} alt={im.alt} className="h-full w-full object-cover" loading="lazy" />
                  {active && <span className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-emerald-200/60" />}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function ToolBtn({ onClick, label, children }) {
  return (
    <button
      onClick={onClick}
      className="rounded-md bg-white/10 px-2.5 py-2 text-white hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/50"
      aria-label={label}
      title={label}
    >
      {children}
    </button>
  );
}

function SideNav({ onClick, children, right, aria }) {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      aria-label={aria}
      className={`absolute top-1/2 z-10 -translate-y-1/2 ${right ? "right-1 sm:right-2" : "left-1 sm:left-2"} flex w-12 sm:w-14 items-center justify-center rounded-full bg-white/10 p-2 text-white hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/50`}
    >
      {children}
    </button>
  );
}
